import { faChevronLeft, faChevronRight } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  DPCalendar,
  DPDay,
  useContextCalendars,
  useContextDatePickerOffsetPropGetters,
  useContextDaysPropGetters,
} from '@rehookify/datepicker'
import React from 'react'
import { twMerge } from 'tailwind-merge'

import Button from '@unreserved-frontend-v2/ui/button/button'
import { Month } from '@unreserved-frontend-v2/ui/date-picker/month'
import { Year } from '@unreserved-frontend-v2/ui/date-picker/year'

interface CalendarProps {
  /**
   * The calendar to display
   */
  calendar: DPCalendar
}

/**
 * A calendar that displays the days of a month and selectors to switch months
 *
 * This component requires a DatePickerStateProvider from @rehookify/datepicker
 */
const Calendar: React.FC<CalendarProps> = ({ calendar }) => {
  const { weekDays } = useContextCalendars()
  const { dayButton } = useContextDaysPropGetters()
  const { addOffset, subtractOffset } = useContextDatePickerOffsetPropGetters()
  const { days, month, year } = calendar

  const determineButtonVariant = (day: DPDay): string => {
    if (day.selected) {
      return 'primary'
    }

    if (day.range === 'will-be-in-range' || day.range === 'in-range') {
      return 'selected'
    }

    return 'ghostLight'
  }

  return (
    <div className="h-[425px] max-w-[390px] p-6">
      <div className="mb-3 flex items-center justify-between">
        <Button variant="ghostLight" {...subtractOffset({ months: 1 })} className="rounded-full border-shades-50">
          <FontAwesomeIcon icon={faChevronLeft} />
        </Button>
        <div className="flex gap-[2px] text-lg font-bold">
          <Month month={month} />
          <Year year={year} />
        </div>
        <Button variant="ghostLight" {...addOffset({ months: 1 })} className="rounded-full border-shades-50">
          <FontAwesomeIcon icon={faChevronRight} />
        </Button>
      </div>

      <div className="mb-3 grid h-8 grid-cols-7 items-center gap-y-2 font-medium">
        {weekDays.map((weekDay) => (
          <div className="text-center" key={weekDay}>
            {/* Shorten week day to the first two letters */}
            {weekDay.slice(0, 2)}
          </div>
        ))}
      </div>

      <div className="mb-2 grid h-8 grid-cols-7 items-center gap-y-2 gap-x-2">
        {days.map((day) => (
          <Button
            key={day.$date.toString()}
            variant={determineButtonVariant(day) as 'primary' | 'ghostLight' | 'selected'}
            className={twMerge(
              'p-3',
              day.inCurrentMonth || day.range === 'in-range' ? '' : 'border-none text-shades-100 hover:text-white',
              day.selected ? 'text-white' : ''
            )}
            {...dayButton(day)}
          >
            {day.day}
          </Button>
        ))}
      </div>
    </div>
  )
}

export default Calendar
